import Cookies from 'js-cookie';
import { loginUser, registerUser, forgotPassword } from '../api/authApi';

/**
 * Extract Google profile fields from Google JWT ID Token (credential).
 * Extracts: email, given_name, family_name, name, picture, sub, email_verified
 */
export const parseGoogleJwt = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const parsed = JSON.parse(jsonPayload);
    console.log("GOOGLE PAYLOAD:", parsed);
    return extractGoogleProfile(parsed);
  } catch (error) {
    console.error('Failed to parse Google JWT credential:', error);
    return null;
  }
};

/**
 * Standardize extracted Google profile fields
 */
export const extractGoogleProfile = (data) => {
  if (!data) return null;

  const email = (data.email || '').trim().toLowerCase();
  const given_name = data.given_name || (data.name ? data.name.split(' ')[0] : 'Taza');
  const family_name = data.family_name || (data.name ? data.name.split(' ').slice(1).join(' ') : '') || '';
  const name = data.name || `${given_name} ${family_name}`.trim();
  const picture = data.picture || data.avatar || data.image || data.picture_url || '';
  const sub = data.sub || data.id || '';
  const email_verified = Boolean(data.email_verified || data.verified_email || true);
  const mobile = data.phone_number || data.phoneNumber || data.mobile || data.phone || '';

  return {
    email,
    given_name,
    family_name,
    name,
    picture,
    sub,
    email_verified,
    mobile
  };
};

/**
 * Generates an internal secure password for Google users.
 * Uses Google `sub` (unique user ID) to generate a deterministic password.
 */
export const generateGoogleUserPassword = (sub) => {
  const safeSub = sub || ('GAuth' + Date.now());
  return `GAuth_${safeSub}_Sec#2026!`;
};

/**
 * Determine whether user already exists.
 * Calls forgotPassword(email) or helper method to check email existence on backend.
 */
export const checkUserExists = async (email) => {
  if (!email) return { exists: false };
  try {
    const result = await forgotPassword(email);
    // If forgotPassword returns success, account exists
    if (result.success) {
      return { exists: true };
    }
    // Check message for specific indications
    const msg = (result.message || '').toLowerCase();
    if (msg.includes('not found') || msg.includes('not registered') || msg.includes('does not exist')) {
      return { exists: false };
    }
    return { exists: false };
  } catch (error) {
    console.error('Error checking user existence:', error);
    return { exists: false };
  }
};

/**
 * Complete Google Sign-In flow for new or existing users.
 * 
 * @param {Object} googleProfile Extracted Google profile { email, given_name, family_name, name, picture, sub, email_verified, mobile }
 * @param {Function} authLogin Context login function from useAuth()
 * @returns {Promise<{ success: boolean, isNewUser?: boolean, user?: object, token?: string, message?: string }>}
 */
export const handleGoogleAuthResponse = async (googleProfile, authLogin) => {
  if (!googleProfile || !googleProfile.email) {
    return { success: false, message: 'Invalid Google account information.' };
  }

  const { email, given_name, family_name, sub, picture, mobile } = googleProfile;
  const googlePassword = generateGoogleUserPassword(sub);

  try {
    // Step 1: Attempt login first (for existing Google users)
    console.log('Attempting Google login for:', email);
    const loginResult = await loginUser({
      email,
      password: googlePassword,
      fcm_token: 'NA'
    });

    if (loginResult.success) {
      console.log('Google login successful for existing user.');
      if (loginResult.token) {
        Cookies.set('authToken', loginResult.token, { expires: 7 });
      }
      if (typeof authLogin === 'function') {
        authLogin(loginResult.data, loginResult.token);
      }
      return {
        success: true,
        isNewUser: false,
        user: loginResult.data,
        token: loginResult.token,
        message: 'Google login successful!'
      };
    }

    // Step 2: Register new Google user (bypassing OTP)
    console.log('Registering new Google user:', email);
    const registrationPayload = {
      email: email.trim().toLowerCase(),
      first_name: (given_name || 'Taza').trim(),
      last_name: (family_name || 'User').trim(),
      password: googlePassword,
      mobile: (mobile || sub).trim(),
      country_code: '+1',
      fcm_token: 'NA',
      type: 'user',
      lat: '',
      lng: '',
      cover: picture || 'NA',
      image: picture || 'NA',
      others: '',
      date: '',
      stripe_key: 'NA',
      check: false,
      status: 1,
      referral_code: ''
    };

    const regResult = await registerUser(registrationPayload);

    if (regResult.success) {
      console.log('New Google user registered successfully.');
      const finalToken = regResult.token;
      const finalUser = regResult.data;

      if (finalToken) {
        Cookies.set('authToken', finalToken, { expires: 7 });
      }
      if (typeof authLogin === 'function') {
        authLogin(finalUser, finalToken);
      }
      return {
        success: true,
        isNewUser: true,
        user: finalUser,
        token: finalToken,
        message: 'Account created and logged in with Google! Please update your mobile number.'
      };
    } else {
      const errMsg = regResult.message || 'Registration failed with Google.';
      console.error('Google user registration failed:', errMsg, regResult.errors);

      if (errMsg.toLowerCase().includes('already taken') || errMsg.toLowerCase().includes('already exists')) {
        return {
          success: false,
          message: 'An account with this email already exists. Please log in using your password.'
        };
      }

      return {
        success: false,
        message: errMsg
      };
    }
  } catch (error) {
    console.error('Error in handleGoogleAuthResponse:', error);
    return {
      success: false,
      message: error.message || 'An error occurred during Google Sign-In.'
    };
  }
};
